/**
 * Flashcard Types
 * Định nghĩa types cho hệ thống flashcard
 */

export interface Flashcard {
  id: string;
  authorId: string;
  userId?: string; // For backward compatibility
  title: string;
  front: string; // Câu hỏi/Thuật ngữ
  back: string; // Câu trả lời/Định nghĩa
  lessonId?: string;
  documentId?: string; // For document-based flashcards
  documentName?: string; // Name of the document
  tags?: string[];
  difficulty: 'easy' | 'medium' | 'hard';
  isPublic: boolean;
  views: number;
  likes: number;
  reviewCount?: number; // Number of times reviewed
  confidence?: number; // User confidence level (0-5)
  nextReview?: Date; // Next review date
  lastReviewed?: Date; // Last review date
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateFlashcardInput {
  title: string;
  front: string;
  back: string;
  lessonId?: string;
  tags?: string[];
  difficulty?: 'easy' | 'medium' | 'hard';
  isPublic?: boolean;
}

export interface UpdateFlashcardInput {
  title?: string;
  front?: string;
  back?: string;
  lessonId?: string;
  tags?: string[];
  difficulty?: 'easy' | 'medium' | 'hard';
  isPublic?: boolean;
  reviewCount?: number;
  confidence?: number;
  nextReview?: Date;
  lastReviewed?: Date; // Track last review date
}

// FlashcardDraft - Draft flashcard before saving
export interface FlashcardDraft {
  id?: string;
  userId: string;
  documentId: string;
  documentName: string;
  front: string;
  back: string;
  tags?: string[];
  difficulty: 'easy' | 'medium' | 'hard';
  reviewCount?: number;
  confidence?: number;
  isEdited?: boolean; // Track if draft has been edited
  source?: string; // Source of the flashcard (e.g., 'ai', 'manual')
}

// PDFDocument - PDF document metadata
export interface PDFDocument {
  id: string;
  userId: string;
  name: string;
  filename: string;
  size: number;
  pageCount: number;
  uploadedAt: Date;
  flashcardCount: number;
}

// FlashcardCollection - Collection of flashcards
export interface FlashcardCollection {
  id: string;
  userId: string;
  name: string;
  description?: string;
  flashcardIds: string[];
  createdAt: Date;
  updatedAt: Date;
}

// Generate Flashcards Request
export interface GenerateFlashcardsRequest {
  documentId: string;
  documentName: string;
  content: string;
  count?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  language?: string;
  keywords?: string[]; // Optional keywords for generation
}

// Generate Flashcards Response
export interface GenerateFlashcardsResponse {
  success: boolean;
  flashcards?: FlashcardDraft[]; // Main property
  drafts?: FlashcardDraft[]; // Alternative property name (backward compatibility)
  message?: string;
  error?: string;
  tokensUsed?: number; // Track API token usage
}

// Helper type for difficulty as number (backward compatibility)
export type DifficultyNumber = 1 | 2 | 3;

// Helper functions to convert between difficulty formats
export function difficultyToNumber(difficulty: 'easy' | 'medium' | 'hard'): DifficultyNumber {
  const map: Record<'easy' | 'medium' | 'hard', DifficultyNumber> = {
    'easy': 1,
    'medium': 2,
    'hard': 3
  };
  return map[difficulty];
}

export function numberToDifficulty(num: DifficultyNumber): 'easy' | 'medium' | 'hard' {
  const map: Record<DifficultyNumber, 'easy' | 'medium' | 'hard'> = {
    1: 'easy',
    2: 'medium',
    3: 'hard'
  };
  return map[num];
}
